import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { BarChart3, CalendarDays, Layers, LayoutList, Leaf, Link2, Users } from 'lucide-react';
import type { Facilitator, GameSession, SessionProgram } from '@business-game/shared';
import { LanguageFlag } from '../../components/admin/LanguageFlag';
import { SESSION_LANGUAGE_OPTIONS, timezoneShortLabel } from '../../components/admin/sessionFormOptions';
import {
  buildMonthlySeries,
  exportMonthCsv,
  extractPlannedSlots,
  filterPlayedSessions,
  filterScheduledSessions,
  normalizeSessions,
  type PlannedSlotEntry,
} from './sessionStats';

type Props = {
  sessions: GameSession[];
  programs?: SessionProgram[];
  facilitators?: Facilitator[];
  showSuperAdminInsights?: boolean;
  onEditSession?: (session: GameSession) => void;
  onArchiveSession?: (session: GameSession) => void;
  onArchiveSessions?: (sessions: GameSession[]) => void;
  onUnarchiveSession?: (session: GameSession) => void;
  onDeleteSession?: (session: GameSession) => void;
  onDeleteSessions?: (sessions: GameSession[]) => void;
  /** Coche manuelle « mail intervenant envoyé » (absent = lecture seule). */
  onToggleTrainerEmail?: (session: GameSession, sent: boolean) => void;
};

type ActivityCell = {
  isoDate: string;
  playedCount: number;
  plannedCount: number;
  isCurrentMonth: boolean;
};

type PilotageTab = 'sessions' | 'stats' | 'calendars';

type NormalizedSession = {
  session: GameSession;
  anchorDate: Date;
  players: number;
  completed: number;
};

const GAME_MODE_LABELS: Record<string, string> = {
  teamlab: 'Team Lab',
  leadlab: 'Lead Lab',
};

const formatGameMode = (mode: string): string => GAME_MODE_LABELS[mode] ?? mode;

const getLanguageOption = (language: string | null | undefined) =>
  SESSION_LANGUAGE_OPTIONS.find((option) => option.value === language);

const SessionMetaBadge: React.FC<{ children: React.ReactNode; className?: string; title?: string }> = ({
  children,
  className = '',
  title,
}) => (
  <span
    title={title}
    className={`inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${className}`}
  >
    {children}
  </span>
);

const SessionCodeChip: React.FC<{
  code: string;
  copiedCode: string | null;
  onCopy: (code: string) => void;
}> = ({ code, copiedCode, onCopy }) => {
  const copied = copiedCode === code;

  return (
    <span
      className={[
        'inline-flex h-8 shrink-0 items-stretch overflow-hidden rounded-lg border',
        copied
          ? 'border-emerald-200 bg-emerald-50 ring-1 ring-emerald-100'
          : 'border-slate-200 bg-white ring-1 ring-slate-100',
      ].join(' ')}
      onClick={(event) => event.stopPropagation()}
    >
      <span className="inline-flex w-[3.25rem] items-center justify-center font-mono text-xs font-bold tracking-[0.22em] tabular-nums text-slate-800">
        {code}
      </span>
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          onCopy(code);
        }}
        className={[
          'inline-flex h-8 w-8 shrink-0 items-center justify-center border-l transition-colors',
          copied
            ? 'border-emerald-200 bg-emerald-100 text-emerald-700'
            : 'border-slate-200 bg-slate-50 text-slate-500 hover:bg-slate-100 hover:text-slate-700',
        ].join(' ')}
        title={copied ? 'Code copié' : 'Copier le code'}
        aria-label={copied ? 'Code copié' : 'Copier le code de session'}
      >
          {copied ? (
            <svg className="h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          ) : (
            <svg className="h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15.75 17.25v3.375c0 .621-.504 1.125-1.125 1.125H5.625c-.621 0-1.125-.504-1.125-1.125V7.875c0-.621.504-1.125 1.125-1.125h3.375M15.75 17.25H18a2.25 2.25 0 002.25-2.25V5.625c0-.621-.504-1.125-1.125-1.125H9.375c-.621 0-1.125.504-1.125 1.125v2.25M15.75 17.25V9.375c0-.621.504-1.125 1.125-1.125h3.375c.621 0 1.125.504 1.125 1.125v8.25"
              />
            </svg>
          )}
        </button>
    </span>
  );
};

const SessionModeAvatar: React.FC<{ session: GameSession }> = ({ session }) => {
  const isLead = session.gameMode === 'leadlab';
  const isLive = !session.isClosed && !session.archivedAt;

  return (
    <div
      className={[
        'relative flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl',
        isLead
          ? 'bg-gradient-to-br from-emerald-100 via-teal-50 to-white text-emerald-700 ring-2 ring-emerald-200/90'
          : 'bg-gradient-to-br from-violet-100 via-indigo-50 to-white text-violet-700 ring-2 ring-violet-200/90',
        isLive ? 'shadow-md shadow-violet-200/50' : '',
      ].join(' ')}
      title={formatGameMode(session.gameMode)}
    >
      {isLead ? <Leaf className="h-6 w-6" strokeWidth={2.2} aria-hidden="true" /> : <Users className="h-6 w-6" strokeWidth={2.2} aria-hidden="true" />}
      {isLive ? <span className="absolute -right-0.5 -top-0.5 h-3 w-3 rounded-full border-2 border-white bg-emerald-500" aria-hidden="true" /> : null}
    </div>
  );
};

const SessionMainCell: React.FC<{
  session: GameSession;
  copiedCode: string | null;
  onCopy: (code: string) => void;
  subtitle?: string;
}> = ({ session, copiedCode, onCopy, subtitle }) => {
  const languageOption = getLanguageOption(session.language);
  const metaItems: string[] = [];

  if (session.notes) {
    metaItems.push(session.notes);
  }
  if (session.programName) {
    metaItems.push(session.programName);
  }
  if (session.programSlotLabel) {
    metaItems.push(session.programSlotLabel);
  }

  return (
    <td className="px-4 py-2.5 align-middle">
      <div className="flex min-w-0 items-center gap-3">
        <SessionModeAvatar session={session} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
            <span className="truncate text-sm font-bold text-slate-900">{session.name || 'Sans nom'}</span>
            {session.isTest ? (
              <SessionMetaBadge className="bg-amber-100 text-amber-800" title="Session de test">
                Test
              </SessionMetaBadge>
            ) : null}
            {languageOption ? (
              <LanguageFlag
                countryCode={languageOption.countryCode}
                title={languageOption.label}
                className="h-3 w-4 shrink-0 rounded-sm opacity-75 ring-1 ring-slate-200/70"
              />
            ) : null}
          </div>
          {subtitle ? <div className="mt-0.5 truncate text-[11px] text-slate-400">{subtitle}</div> : null}
          {metaItems.length > 0 ? (
            <div className="mt-0.5 truncate text-[11px] text-slate-400">{metaItems.join(' · ')}</div>
          ) : null}
        </div>
        <div className="shrink-0 self-center">
          <SessionCodeChip code={session.code} copiedCode={copiedCode} onCopy={onCopy} />
        </div>
      </div>
    </td>
  );
};

const formatCompactSchedule = (session: GameSession, anchorDate: Date): string => {
  const date = anchorDate.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
  });

  if (session.startDate) {
    const time = anchorDate.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    return `${date} · ${time}`;
  }

  return date;
};

const sessionStatus = (session: GameSession, players: number): { label: string; badgeClass: string } => {
  if (session.isClosed) return { label: 'fermée', badgeClass: 'bg-slate-200 text-slate-600' };
  if (players > 0) return { label: 'jouée', badgeClass: 'bg-emerald-100 text-emerald-700' };
  if (session.startDate && new Date(session.startDate) > new Date()) {
    return { label: 'planifiée', badgeClass: 'bg-sky-100 text-sky-700' };
  }
  return { label: 'ouverte', badgeClass: 'bg-indigo-100 text-indigo-700' };
};

const SessionStatusCell: React.FC<{ session: GameSession; players: number; completed: number }> = ({
  session,
  players,
  completed,
}) => (
  <td className="px-4 py-2.5 align-middle whitespace-nowrap">
    <div className="flex items-center gap-2">
      <span
        className={[
          'inline-flex shrink-0 items-center rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide',
          sessionStatus(session, players).badgeClass,
        ].join(' ')}
      >
        {sessionStatus(session, players).label}
      </span>
      <span className="text-xs tabular-nums text-slate-500">
        {players} j · {completed} fini{completed !== 1 ? 's' : ''}
      </span>
    </div>
  </td>
);

const SESSION_ACTION_BTN =
  'inline-flex h-8 w-8 shrink-0 items-center justify-center text-slate-400 transition-colors hover:bg-slate-50 hover:text-slate-700 focus:relative focus:z-10 focus:outline-none';

const SessionActionGroup: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="inline-flex overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm divide-x divide-slate-200">
    {children}
  </div>
);

const SessionActionGroupButton: React.FC<{
  title: string;
  onClick?: () => void;
  href?: string;
  className?: string;
  children: React.ReactNode;
}> = ({ title, onClick, href, className = '', children }) => {
  const cls = `${SESSION_ACTION_BTN} ${className}`.trim();

  if (href) {
    return (
      <a href={href} target="_blank" rel="noreferrer" className={cls} title={title} aria-label={title}>
        {children}
      </a>
    );
  }

  return (
    <button type="button" onClick={onClick} className={cls} title={title} aria-label={title}>
      {children}
    </button>
  );
};

const SessionActionsCell: React.FC<{
  session: GameSession;
  onEditSession?: (session: GameSession) => void;
  onArchiveSession?: (session: GameSession) => void;
  onUnarchiveSession?: (session: GameSession) => void;
  onDeleteSession?: (session: GameSession) => void;
  navigate: ReturnType<typeof useNavigate>;
}> = ({
  session,
  onEditSession,
  onArchiveSession,
  onUnarchiveSession,
  onDeleteSession,
  navigate,
}) => (
  <td className="px-4 py-2.5 align-middle text-right" onClick={(event) => event.stopPropagation()}>
    <SessionActionGroup>
      {onEditSession ? (
        <SessionActionGroupButton
          title="Modifier"
          onClick={() => onEditSession(session)}
          className="hover:text-indigo-600 hover:bg-indigo-50/60"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
        </SessionActionGroupButton>
      ) : null}
      <SessionActionGroupButton
        title="Page joueur"
        href={`/select-player/${session.code}`}
        className="hover:text-indigo-600 hover:bg-indigo-50/60"
      >
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
        </svg>
      </SessionActionGroupButton>
      <SessionActionGroupButton
        title="Statistiques"
        onClick={() => navigate(`/admin/decisions/1?session=${session.code}`)}
        className="hover:text-violet-600 hover:bg-violet-50/60"
      >
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      </SessionActionGroupButton>
      {onArchiveSession ? (
        <SessionActionGroupButton
          title="Archiver"
          onClick={() => onArchiveSession(session)}
          className="hover:text-amber-600 hover:bg-amber-50/60"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 7h16m-1 0v10a2 2 0 01-2 2H7a2 2 0 01-2-2V7m3-3h8l1 3H7l1-3z" />
          </svg>
        </SessionActionGroupButton>
      ) : null}
      {onUnarchiveSession ? (
        <SessionActionGroupButton
          title="Désarchiver"
          onClick={() => onUnarchiveSession(session)}
          className="hover:text-emerald-600 hover:bg-emerald-50/60"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 8l2-3h10l2 3M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8M9 12h6" />
          </svg>
        </SessionActionGroupButton>
      ) : null}
      {onDeleteSession ? (
        <SessionActionGroupButton
          title="Supprimer"
          onClick={() => onDeleteSession(session)}
          className="hover:text-red-600 hover:bg-red-50/60"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        </SessionActionGroupButton>
      ) : null}
    </SessionActionGroup>
  </td>
);

const getActivityTone = (count: number): string => {
  if (count === 0) return 'bg-slate-100 border-slate-200';
  if (count === 1) return 'bg-indigo-100 border-indigo-200';
  if (count === 2) return 'bg-indigo-300 border-indigo-400';
  return 'bg-indigo-500 border-indigo-500';
};

const getPlannedTone = (count: number): string => {
  if (count === 0) return 'bg-slate-100 border-slate-200';
  if (count === 1) return 'bg-emerald-50 border-emerald-300 border-dashed';
  if (count === 2) return 'bg-emerald-100 border-emerald-400 border-dashed';
  return 'bg-emerald-200 border-emerald-500 border-dashed';
};

const getCalendarCellClass = (cell: ActivityCell): string => {
  if (!cell.isCurrentMonth) {
    return 'bg-transparent border-transparent text-slate-300';
  }

  const { playedCount, plannedCount } = cell;
  if (playedCount > 0 && plannedCount > 0) {
    return `${getActivityTone(playedCount)} ring-2 ring-emerald-300/70`;
  }
  if (playedCount > 0) {
    return getActivityTone(playedCount);
  }
  if (plannedCount > 0) {
    return getPlannedTone(plannedCount);
  }
  return 'bg-slate-100 border-slate-200';
};

const buildMonthCalendar = (monthStart: Date, dailyCounts: Map<string, { played: number; planned: number }>): ActivityCell[] => {
  const year = monthStart.getFullYear();
  const month = monthStart.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const offset = (firstDay.getDay() + 6) % 7;
  const daysInMonth = lastDay.getDate();
  const totalCells = Math.ceil((offset + daysInMonth) / 7) * 7;
  const cells: ActivityCell[] = [];

  for (let index = 0; index < totalCells; index += 1) {
    const cellDate = new Date(year, month, index - offset + 1);
    const isoDate = cellDate.toISOString().slice(0, 10);

    cells.push({
      isoDate,
      playedCount: dailyCounts.get(isoDate)?.played ?? 0,
      plannedCount: dailyCounts.get(isoDate)?.planned ?? 0,
      isCurrentMonth: cellDate.getMonth() === month,
    });
  }

  return cells;
};

const ITEMS_PER_PAGE_OPTIONS = [10, 25, 50, 100] as const;

type ProgramFilter = 'all' | 'none' | number;
type FacilitatorFilter = 'all' | 'none' | string;

const PROGRAM_FILTER_PARAM = 'program';
const FACILITATOR_FILTER_PARAM = 'animateur';
const SEARCH_FILTER_PARAM = 'q';

const parseProgramFilterParam = (raw: string | null): ProgramFilter => {
  if (!raw || raw === 'all') return 'all';
  if (raw === 'none') return 'none';
  const id = Number(raw);
  return Number.isInteger(id) && id > 0 ? id : 'all';
};

const parseFacilitatorFilterParam = (raw: string | null): FacilitatorFilter => {
  if (!raw || raw === 'all') return 'all';
  if (raw === 'none') return 'none';
  return raw;
};

const buildSessionFilterParams = (
  programFilter: ProgramFilter,
  facilitatorFilter: FacilitatorFilter,
  searchQuery: string
): URLSearchParams => {
  const params = new URLSearchParams();
  if (programFilter !== 'all') {
    params.set(PROGRAM_FILTER_PARAM, programFilter === 'none' ? 'none' : String(programFilter));
  }
  if (facilitatorFilter !== 'all') {
    params.set(
      FACILITATOR_FILTER_PARAM,
      facilitatorFilter === 'none' ? 'none' : facilitatorFilter
    );
  }
  const q = searchQuery.trim();
  if (q) params.set(SEARCH_FILTER_PARAM, q);
  return params;
};

const getSessionFacilitatorName = (session: GameSession): string => (session.notes || '').trim();

const applyProgramFilter = (items: NormalizedSession[], programFilter: ProgramFilter): NormalizedSession[] => {
  if (programFilter === 'none') {
    return items.filter((item) => !item.session.programId);
  }
  if (programFilter !== 'all') {
    return items.filter((item) => item.session.programId === programFilter);
  }
  return items;
};

const applyFacilitatorFilter = (
  items: NormalizedSession[],
  facilitatorFilter: FacilitatorFilter
): NormalizedSession[] => {
  if (facilitatorFilter === 'none') {
    return items.filter((item) => !getSessionFacilitatorName(item.session));
  }
  if (facilitatorFilter !== 'all') {
    return items.filter((item) => getSessionFacilitatorName(item.session) === facilitatorFilter);
  }
  return items;
};

const ProgramFilterDropdown: React.FC<{
  programs: SessionProgram[];
  value: ProgramFilter;
  onChange: (value: ProgramFilter) => void;
}> = ({ programs, value, onChange }) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const selectedLabel =
    value === 'all'
      ? 'Tous les programmes'
      : value === 'none'
        ? 'Hors programme'
        : programs.find((program) => program.id === value)?.name ?? 'Programme';

  const options: Array<{ value: ProgramFilter; label: string; hint?: string }> = [
    { value: 'all', label: 'Tous les programmes' },
    { value: 'none', label: 'Hors programme', hint: 'Sessions sans programme lié' },
    ...programs.map((program) => ({
      value: program.id as ProgramFilter,
      label: program.name,
      hint: `${program.slots.length} séance${program.slots.length > 1 ? 's' : ''} · ${formatGameMode(program.gameMode)}`,
    })),
  ];

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        className={[
          'inline-flex min-w-[10rem] items-center gap-2 rounded-xl border px-3 py-2 text-sm font-semibold transition-colors',
          open || value !== 'all'
            ? 'border-violet-300 bg-violet-50 text-violet-800'
            : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50',
        ].join(' ')}
      >
        <Layers className="h-4 w-4 shrink-0" aria-hidden="true" />
        <span className="truncate">{selectedLabel}</span>
        <svg
          className={`ml-auto h-4 w-4 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open ? (
        <div className="absolute left-0 z-30 mt-2 max-h-80 w-80 overflow-y-auto rounded-2xl border border-violet-200 bg-white p-2 shadow-xl shadow-violet-500/10">
          {options.map((option) => {
            const active = option.value === value;
            return (
              <button
                key={String(option.value)}
                type="button"
                onClick={() => {
                  onChange(option.value);
                  setOpen(false);
                }}
                className={[
                  'flex w-full flex-col rounded-xl px-3 py-2.5 text-left transition-colors',
                  active ? 'bg-violet-50 text-violet-800' : 'text-slate-700 hover:bg-slate-50',
                ].join(' ')}
              >
                <span className="text-sm font-bold">{option.label}</span>
                {option.hint ? <span className="mt-0.5 text-xs text-slate-500">{option.hint}</span> : null}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
};

const FacilitatorFilterDropdown: React.FC<{
  options: Array<{ value: string; label: string; hint?: string }>;
  value: FacilitatorFilter;
  onChange: (value: FacilitatorFilter) => void;
}> = ({ options, value, onChange }) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const selectedLabel =
    value === 'all'
      ? 'Tous les animateurs'
      : value === 'none'
        ? 'Sans animateur'
        : value;

  const dropdownOptions: Array<{ value: FacilitatorFilter; label: string; hint?: string }> = [
    { value: 'all', label: 'Tous les animateurs' },
    { value: 'none', label: 'Sans animateur', hint: 'Sessions sans facilitateur renseigné' },
    ...options.map((option) => ({
      value: option.value,
      label: option.label,
      hint: option.hint,
    })),
  ];

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        className={[
          'inline-flex min-w-[10rem] items-center gap-2 rounded-xl border px-3 py-2 text-sm font-semibold transition-colors',
          open || value !== 'all'
            ? 'border-violet-300 bg-violet-50 text-violet-800'
            : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50',
        ].join(' ')}
      >
        <Users className="h-4 w-4 shrink-0" aria-hidden="true" />
        <span className="truncate">{selectedLabel}</span>
        <svg
          className={`ml-auto h-4 w-4 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open ? (
        <div className="absolute left-0 z-30 mt-2 max-h-80 w-80 overflow-y-auto rounded-2xl border border-violet-200 bg-white p-2 shadow-xl shadow-violet-500/10">
          {dropdownOptions.map((option) => {
            const active = option.value === value;
            return (
              <button
                key={String(option.value)}
                type="button"
                onClick={() => {
                  onChange(option.value);
                  setOpen(false);
                }}
                className={[
                  'flex w-full flex-col rounded-xl px-3 py-2.5 text-left transition-colors',
                  active ? 'bg-violet-50 text-violet-800' : 'text-slate-700 hover:bg-slate-50',
                ].join(' ')}
              >
                <span className="text-sm font-bold">{option.label}</span>
                {option.hint ? <span className="mt-0.5 text-xs text-slate-500">{option.hint}</span> : null}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
};

type SortKey = 'name' | 'program' | 'facilitator' | 'createdAt' | 'anchorDate' | 'status' | 'players';
type SortDir = 'asc' | 'desc';

const SortIcon: React.FC<{ dir: SortDir | null; isActive: boolean }> = ({ dir, isActive }) => (
  <span
    className={`inline-flex flex-col ml-1.5 transition-colors ${
      isActive ? 'text-indigo-600' : 'text-slate-300 group-hover:text-slate-400'
    }`}
  >
    <svg
      className={`w-2.5 h-2.5 transition-opacity ${dir === 'asc' ? 'opacity-100' : 'opacity-30'}`}
      fill="currentColor"
      viewBox="0 0 20 20"
    >
      <path d="M5 12l5-5 5 5H5z" />
    </svg>
    <svg
      className={`w-2.5 h-2.5 -mt-0.5 transition-opacity ${dir === 'desc' ? 'opacity-100' : 'opacity-30'}`}
      fill="currentColor"
      viewBox="0 0 20 20"
    >
      <path d="M15 8l-5 5-5-5h10z" />
    </svg>
  </span>
);

const SortableTh: React.FC<{
  label: string;
  sortKey: SortKey;
  currentSort: SortKey | null;
  sortDir: SortDir;
  onSort: (key: SortKey) => void;
  className?: string;
}> = ({ label, sortKey, currentSort, sortDir, onSort, className = '' }) => {
  const isActive = currentSort === sortKey;
  const dir = isActive ? sortDir : null;
  return (
    <th className={className}>
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className="group inline-flex items-center font-bold uppercase tracking-wider transition-all hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:ring-offset-1 rounded-lg px-2 -mx-2 py-1 -my-1 hover:bg-slate-50"
      >
        <span className={isActive ? 'text-indigo-600' : 'text-slate-400'}>{label}</span>
        <SortIcon dir={dir} isActive={isActive} />
      </button>
    </th>
  );
};

export const SuperAdminOverview: React.FC<Props> = ({
  sessions,
  programs = [],
  facilitators = [],
  showSuperAdminInsights = false,
  onEditSession,
  onArchiveSession,
  onArchiveSessions,
  onUnarchiveSession,
  onDeleteSession,
  onDeleteSessions,
  onToggleTrainerEmail,
}) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const monthlyChartRef = useRef<HTMLDivElement>(null);
  const monthlyChartInstanceRef = useRef<any>(null);
  const [calendarMonthOffset, setCalendarMonthOffset] = useState(0);
  const [selectedDayIso, setSelectedDayIso] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<PilotageTab>('sessions');

  // Table filters & pagination
  const [searchQuery, setSearchQuery] = useState(() => searchParams.get(SEARCH_FILTER_PARAM) ?? '');
  const [programFilter, setProgramFilter] = useState<ProgramFilter>(() =>
    parseProgramFilterParam(searchParams.get(PROGRAM_FILTER_PARAM))
  );
  const [facilitatorFilter, setFacilitatorFilter] = useState<FacilitatorFilter>(() =>
    parseFacilitatorFilterParam(searchParams.get(FACILITATOR_FILTER_PARAM))
  );
  const [showArchivedAccordion, setShowArchivedAccordion] = useState(false);
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [sortKey, setSortKey] = useState<SortKey>('createdAt');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [copiedFilterLink, setCopiedFilterLink] = useState(false);
  const [selectedArchivedIds, setSelectedArchivedIds] = useState<Set<number>>(() => new Set());
  const [selectedVisibleIds, setSelectedVisibleIds] = useState<Set<number>>(() => new Set());

  const canBulkDeleteArchived = !!onDeleteSessions;
  const canBulkArchiveVisible = !!onArchiveSessions;
  const hasActiveFilters =
    programFilter !== 'all' || facilitatorFilter !== 'all' || !!searchQuery.trim();

  const copyFilteredViewLink = async () => {
    const params = buildSessionFilterParams(programFilter, facilitatorFilter, searchQuery);
    const url = `${window.location.origin}/admin/sessions${params.toString() ? `?${params.toString()}` : ''}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedFilterLink(true);
      window.setTimeout(() => setCopiedFilterLink(false), 2000);
    } catch (error) {
      console.error('Failed to copy filtered view link:', error);
    }
  };

  const copySessionCode = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (error) {
      console.error('Failed to copy session code:', error);
    }
  };

  const normalizedSessions = useMemo(
    () => normalizeSessions(sessions),
    [sessions]
  );

  const handleSort = (key: SortKey) => {
    setSortKey(key);
    setSortDir((d) => (sortKey === key && d === 'desc' ? 'asc' : 'desc'));
    setPage(1);
  };

  const playedSessions = useMemo(() => filterPlayedSessions(normalizedSessions), [normalizedSessions]);
  const scheduledSessions = useMemo(() => filterScheduledSessions(normalizedSessions), [normalizedSessions]);
  const plannedSlots = useMemo(() => extractPlannedSlots(programs), [programs]);

  const filteredSessions = useMemo(() => {
    let result = normalizedSessions;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      result = result.filter(
        (item) =>
          item.session.code.toLowerCase().includes(q) ||
          (item.session.name || '').toLowerCase().includes(q) ||
          (item.session.notes || '').toLowerCase().includes(q) ||
          (item.session.programName || '').toLowerCase().includes(q) ||
          (item.session.programSlotLabel || '').toLowerCase().includes(q) ||
          item.session.gameMode.toLowerCase().includes(q)
      );
    }
    return result;
  }, [normalizedSessions, searchQuery]);

  const facilitatorFilterOptions = useMemo(() => {
    const counts = new Map<string, number>();
    filteredSessions.forEach(({ session }) => {
      const name = getSessionFacilitatorName(session);
      if (name) counts.set(name, (counts.get(name) ?? 0) + 1);
    });

    const names = new Set<string>();
    facilitators.forEach((facilitator) => names.add(facilitator.name));
    counts.forEach((_, name) => names.add(name));

    return [...names]
      .sort((a, b) => a.localeCompare(b, 'fr'))
      .map((name) => {
        const count = counts.get(name) ?? 0;
        return {
          value: name,
          label: name,
          hint: `${count} session${count > 1 ? 's' : ''}`,
        };
      });
  }, [facilitators, filteredSessions]);

  const visibleSessions = useMemo(() => {
    let result = filteredSessions.filter((item) => !item.session.archivedAt);
    result = applyProgramFilter(result, programFilter);
    result = applyFacilitatorFilter(result, facilitatorFilter);
    return result;
  }, [filteredSessions, programFilter, facilitatorFilter]);

  const archivedSessions = useMemo(() => {
    let result = filteredSessions.filter((item) => !!item.session.archivedAt);
    result = applyProgramFilter(result, programFilter);
    result = applyFacilitatorFilter(result, facilitatorFilter);
    return result;
  }, [filteredSessions, programFilter, facilitatorFilter]);

  const sortSessions = useMemo(() => {
    const mult = sortDir === 'asc' ? 1 : -1;
    return (items: NormalizedSession[]) => [...items].sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case 'name':
          cmp = (a.session.name || '').localeCompare(b.session.name || '');
          break;
        case 'program':
          cmp = (a.session.programName || '').localeCompare(b.session.programName || '');
          break;
        case 'facilitator':
          cmp = (a.session.notes || '').localeCompare(b.session.notes || '');
          break;
        case 'createdAt':
          cmp = new Date(a.session.createdAt).getTime() - new Date(b.session.createdAt).getTime();
          break;
        case 'anchorDate':
          cmp = a.anchorDate.getTime() - b.anchorDate.getTime();
          break;
        case 'status': {
          const statusOrder = (s: GameSession) =>
            s.archivedAt ? 0 : s.isClosed ? 1 : 2;
          cmp = statusOrder(a.session) - statusOrder(b.session);
          break;
        }
        case 'players':
          cmp = a.players - b.players || a.completed - b.completed;
          break;
        default:
          return 0;
      }
      return mult * cmp;
    });
  }, [filteredSessions, sortKey, sortDir]);

  const sortedVisibleSessions = useMemo(() => sortSessions(visibleSessions), [sortSessions, visibleSessions]);
  const visibleSessionIds = useMemo(
    () => sortedVisibleSessions.map(({ session }) => session.id),
    [sortedVisibleSessions]
  );
  const allVisibleSelected =
    visibleSessionIds.length > 0 && visibleSessionIds.every((id) => selectedVisibleIds.has(id));
  const someVisibleSelected = visibleSessionIds.some((id) => selectedVisibleIds.has(id));
  const selectedVisibleSessions = useMemo(
    () => sortedVisibleSessions.filter(({ session }) => selectedVisibleIds.has(session.id)).map(({ session }) => session),
    [sortedVisibleSessions, selectedVisibleIds]
  );
  const selectAllVisibleRef = useRef<HTMLInputElement>(null);

  const toggleVisibleSelection = (sessionId: number) => {
    setSelectedVisibleIds((current) => {
      const next = new Set(current);
      if (next.has(sessionId)) next.delete(sessionId);
      else next.add(sessionId);
      return next;
    });
  };

  const toggleAllVisibleSelection = () => {
    setSelectedVisibleIds((current) => {
      if (allVisibleSelected) {
        const next = new Set(current);
        visibleSessionIds.forEach((id) => next.delete(id));
        return next;
      }
      const next = new Set(current);
      visibleSessionIds.forEach((id) => next.add(id));
      return next;
    });
  };

  const requestArchiveSelectedVisible = () => {
    if (!onArchiveSessions || selectedVisibleSessions.length === 0) return;
    onArchiveSessions(selectedVisibleSessions);
  };

  const sortedArchivedSessions = useMemo(() => sortSessions(archivedSessions), [sortSessions, archivedSessions]);
  const archivedSessionIds = useMemo(
    () => sortedArchivedSessions.map(({ session }) => session.id),
    [sortedArchivedSessions]
  );
  const allArchivedSelected =
    archivedSessionIds.length > 0 && archivedSessionIds.every((id) => selectedArchivedIds.has(id));
  const someArchivedSelected = archivedSessionIds.some((id) => selectedArchivedIds.has(id));
  const selectedArchivedSessions = useMemo(
    () => sortedArchivedSessions.filter(({ session }) => selectedArchivedIds.has(session.id)).map(({ session }) => session),
    [sortedArchivedSessions, selectedArchivedIds]
  );
  const selectAllArchivedRef = useRef<HTMLInputElement>(null);

  const toggleArchivedSelection = (sessionId: number) => {
    setSelectedArchivedIds((current) => {
      const next = new Set(current);
      if (next.has(sessionId)) next.delete(sessionId);
      else next.add(sessionId);
      return next;
    });
  };

  const toggleAllArchivedSelection = () => {
    setSelectedArchivedIds((current) => {
      if (allArchivedSelected) {
        const next = new Set(current);
        archivedSessionIds.forEach((id) => next.delete(id));
        return next;
      }
      const next = new Set(current);
      archivedSessionIds.forEach((id) => next.add(id));
      return next;
    });
  };

  const requestDeleteSelectedArchived = () => {
    if (!onDeleteSessions || selectedArchivedSessions.length === 0) return;
    onDeleteSessions(selectedArchivedSessions);
  };

  const totalPages = Math.max(1, Math.ceil(sortedVisibleSessions.length / itemsPerPage));
  const currentPage = Math.min(page, totalPages);
  const paginatedSessions = useMemo(
    () => sortedVisibleSessions.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage),
    [sortedVisibleSessions, currentPage, itemsPerPage]
  );

  useEffect(() => {
    const next = buildSessionFilterParams(programFilter, facilitatorFilter, searchQuery);
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [programFilter, facilitatorFilter, searchQuery, searchParams, setSearchParams]);

  useEffect(() => {
    setPage(1);
  }, [searchQuery, itemsPerPage, programFilter, facilitatorFilter]);

  useEffect(() => {
    setPage((current) => Math.min(current, Math.max(1, Math.ceil(sortedVisibleSessions.length / itemsPerPage))));
  }, [sortedVisibleSessions.length, itemsPerPage]);

  useEffect(() => {
    setSelectedArchivedIds((current) => {
      const visibleIds = new Set(archivedSessionIds);
      const next = new Set<number>();
      current.forEach((id) => {
        if (visibleIds.has(id)) next.add(id);
      });
      return next.size === current.size ? current : next;
    });
  }, [archivedSessionIds]);

  useEffect(() => {
    setSelectedVisibleIds((current) => {
      const visibleIds = new Set(visibleSessionIds);
      const next = new Set<number>();
      current.forEach((id) => {
        if (visibleIds.has(id)) next.add(id);
      });
      return next.size === current.size ? current : next;
    });
  }, [visibleSessionIds]);

  useEffect(() => {
    const input = selectAllArchivedRef.current;
    if (!input) return;
    input.indeterminate = someArchivedSelected && !allArchivedSelected;
  }, [someArchivedSelected, allArchivedSelected]);

  useEffect(() => {
    const input = selectAllVisibleRef.current;
    if (!input) return;
    input.indeterminate = someVisibleSelected && !allVisibleSelected;
  }, [someVisibleSelected, allVisibleSelected]);

  const calendarDaysByIso = useMemo(() => {
    const dayMap = new Map<
      string,
      { played: NormalizedSession[]; plannedSessions: NormalizedSession[]; plannedSlots: PlannedSlotEntry[] }
    >();

    const ensureDay = (isoDate: string) => {
      const existing = dayMap.get(isoDate) ?? { played: [], plannedSessions: [], plannedSlots: [] };
      dayMap.set(isoDate, existing);
      return existing;
    };

    playedSessions.forEach((item) => {
      const key = item.anchorDate.toISOString().slice(0, 10);
      ensureDay(key).played.push(item);
    });

    scheduledSessions.forEach((item) => {
      const scheduleDate = item.session.startDate ? new Date(item.session.startDate) : item.anchorDate;
      const key = scheduleDate.toISOString().slice(0, 10);
      ensureDay(key).plannedSessions.push(item);
    });

    plannedSlots.forEach((entry) => {
      const key = entry.scheduledAt.toISOString().slice(0, 10);
      ensureDay(key).plannedSlots.push(entry);
    });

    dayMap.forEach((items) => {
      items.played.sort((a, b) => a.anchorDate.getTime() - b.anchorDate.getTime());
      items.plannedSessions.sort(
        (a, b) =>
          new Date(a.session.startDate ?? a.anchorDate).getTime() -
          new Date(b.session.startDate ?? b.anchorDate).getTime()
      );
      items.plannedSlots.sort((a, b) => a.scheduledAt.getTime() - b.scheduledAt.getTime());
    });

    return dayMap;
  }, [playedSessions, scheduledSessions, plannedSlots]);

  const selectedDayDetails = selectedDayIso
    ? calendarDaysByIso.get(selectedDayIso) ?? { played: [], plannedSessions: [], plannedSlots: [] }
    : { played: [], plannedSessions: [], plannedSlots: [] };
  const selectedDayTotal =
    selectedDayDetails.played.length +
    selectedDayDetails.plannedSessions.length +
    selectedDayDetails.plannedSlots.length;

  const summary = useMemo(() => {
    const archived = playedSessions.filter(({ session }) => !!session.archivedAt);
    const tests = playedSessions.filter(({ session }) => session.isTest);
    const players = playedSessions.reduce((acc, item) => acc + item.players, 0);
    const completed = playedSessions.reduce((acc, item) => acc + item.completed, 0);

    return {
      totalSessions: playedSessions.length,
      archivedSessions: archived.length,
      testSessions: tests.length,
      activeSessions: playedSessions.filter(({ session }) => !session.isClosed && !session.archivedAt).length,
      totalPlayers: players,
      totalCompleted: completed,
      completionRate: players > 0 ? Math.round((completed / players) * 100) : 0,
    };
  }, [playedSessions]);

  const monthlySeries = useMemo(
    () => buildMonthlySeries(playedSessions, { type: 'last', count: 6 }),
    [playedSessions]
  );

  const recentCalendars = useMemo(() => {
    const dailyCounts = new Map<string, { played: number; planned: number }>();

    playedSessions.forEach(({ anchorDate }) => {
      const isoDate = anchorDate.toISOString().slice(0, 10);
      const existing = dailyCounts.get(isoDate) ?? { played: 0, planned: 0 };
      existing.played += 1;
      dailyCounts.set(isoDate, existing);
    });

    scheduledSessions.forEach((item) => {
      const scheduleDate = item.session.startDate ? new Date(item.session.startDate) : item.anchorDate;
      const isoDate = scheduleDate.toISOString().slice(0, 10);
      const existing = dailyCounts.get(isoDate) ?? { played: 0, planned: 0 };
      existing.planned += 1;
      dailyCounts.set(isoDate, existing);
    });

    plannedSlots.forEach(({ scheduledAt }) => {
      const isoDate = scheduledAt.toISOString().slice(0, 10);
      const existing = dailyCounts.get(isoDate) ?? { played: 0, planned: 0 };
      existing.planned += 1;
      dailyCounts.set(isoDate, existing);
    });

    const now = new Date();
    return Array.from({ length: 3 }, (_, index) => {
      const monthDelta = index - 1 - calendarMonthOffset;
      const monthStart = new Date(now.getFullYear(), now.getMonth() + monthDelta, 1);
      return {
        key: `${monthStart.getFullYear()}-${monthStart.getMonth()}`,
        title: monthStart.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' }),
        cells: buildMonthCalendar(monthStart, dailyCounts),
      };
    });
  }, [calendarMonthOffset, playedSessions, scheduledSessions, plannedSlots]);

  const handleExportMonth = (monthKey: string) => {
    exportMonthCsv(playedSessions, monthKey);
  };

  const formatFullDate = (isoDate: string) =>
    new Date(`${isoDate}T12:00:00`).toLocaleDateString('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });

  const formatDateTime = (date: Date) =>
    new Date(date).toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

  useEffect(() => {
    const destroyChart = () => {
      if (monthlyChartInstanceRef.current) {
        monthlyChartInstanceRef.current.destroy();
        monthlyChartInstanceRef.current = null;
      }
    };

    if (activeTab !== 'stats' || monthlySeries.length === 0) {
      destroyChart();
      return;
    }

    let cancelled = false;
    const frameId = requestAnimationFrame(() => {
      if (cancelled || !monthlyChartRef.current || monthlySeries.length === 0) {
        return;
      }

      import('apexcharts').then((ApexChartsModule) => {
        if (cancelled || !monthlyChartRef.current) {
          return;
        }

        const ApexCharts = ApexChartsModule.default;
        const chartElement = monthlyChartRef.current;

        destroyChart();

        const chart = new ApexCharts(chartElement, {
          series: [
            { name: 'Sessions', data: monthlySeries.map((item) => item.sessions) },
            { name: 'Joueurs', data: monthlySeries.map((item) => item.players) },
            { name: 'Équipes terminées', data: monthlySeries.map((item) => item.completed) },
          ],
          chart: {
            type: 'line',
            height: 320,
            fontFamily: 'Inter, sans-serif',
            toolbar: { show: false },
            background: 'transparent',
          },
          stroke: {
            curve: 'smooth',
            width: 3,
          },
          colors: ['#4f46e5', '#0f172a', '#10b981'],
          xaxis: {
            categories: monthlySeries.map((item) => item.label),
            labels: {
              style: {
                colors: '#64748b',
                fontSize: '11px',
                fontFamily: 'Inter, sans-serif',
              },
            },
          },
          yaxis: {
            labels: {
              style: {
                colors: '#64748b',
                fontSize: '11px',
                fontFamily: 'Inter, sans-serif',
              },
            },
          },
          grid: {
            borderColor: '#e2e8f0',
            strokeDashArray: 4,
          },
          legend: {
            position: 'top',
            horizontalAlign: 'left',
            fontFamily: 'Inter, sans-serif',
          },
          tooltip: {
            theme: 'light',
          },
        } as any);

        chart.render();
        monthlyChartInstanceRef.current = chart;
      });
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(frameId);
      destroyChart();
    };
  }, [monthlySeries, activeTab]);

  const renderVisibleSessionRow = ({ session, anchorDate, players, completed }: NormalizedSession) => {
    return (
      <tr
        key={session.id}
        onClick={() => onEditSession?.(session)}
        className={[
          'border-b text-slate-700 transition-colors',
          session.isTest ? 'bg-amber-50/60 border-amber-100' : 'border-slate-100',
          onEditSession ? 'cursor-pointer hover:bg-indigo-50/40' : '',
        ].join(' ')}
      >
        {canBulkArchiveVisible ? (
          <td className="w-10 px-4 py-2.5 align-middle" onClick={(event) => event.stopPropagation()}>
            <input
              type="checkbox"
              checked={selectedVisibleIds.has(session.id)}
              onChange={() => toggleVisibleSelection(session.id)}
              aria-label={`Sélectionner ${session.name || session.code}`}
              className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
            />
          </td>
        ) : null}
        <SessionMainCell session={session} copiedCode={copiedCode} onCopy={copySessionCode} />
        <td className="px-4 py-2.5 align-middle whitespace-nowrap text-sm tabular-nums text-slate-600">
          {formatCompactSchedule(session, anchorDate)}
        </td>
        <SessionStatusCell session={session} players={players} completed={completed} />
        <td className="px-4 py-2.5 align-middle text-center" onClick={(event) => event.stopPropagation()}>
          <input
            type="checkbox"
            checked={Boolean(session.trainerEmailSentAt)}
            disabled={!onToggleTrainerEmail}
            onChange={(event) => onToggleTrainerEmail?.(session, event.target.checked)}
            aria-label={`Mail intervenant envoyé pour ${session.name || session.code}`}
            title={
              session.trainerEmailSentAt
                ? `Mail intervenant envoyé le ${new Date(session.trainerEmailSentAt).toLocaleString('fr-FR')}`
                : 'Mail intervenant non envoyé'
            }
            className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 disabled:opacity-60"
          />
        </td>
        <SessionActionsCell
          session={session}
          onEditSession={onEditSession}
          onArchiveSession={onArchiveSession}
          navigate={navigate}
        />
      </tr>
    );
  };

  return (
    <section className="mb-8 rounded-[2rem] border border-violet-200 bg-gradient-to-br from-violet-50 via-white to-slate-50 p-6 shadow-sm">
      {showSuperAdminInsights ? (
        <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.25em] text-violet-600">Super Admin</p>
            <h2 className="mt-2 text-3xl font-black tracking-tight text-slate-900">Pilotage global des sessions</h2>
          </div>
          <div className="rounded-2xl border border-violet-200 bg-white/80 px-4 py-3 text-right">
            <div className="text-xs font-bold uppercase tracking-wider text-slate-400">Taux de complétion global</div>
            <div className="text-3xl font-black text-slate-900">{summary.completionRate}%</div>
          </div>
        </div>
      ) : (
        <div className="mb-2">
          <p className="text-sm text-slate-600">
            Retrouvez vos sessions, filtrez par programme et consultez l’historique archivé.
          </p>
        </div>
      )}

      {showSuperAdminInsights ? (
        <div className="mt-6 flex flex-wrap gap-2 rounded-2xl border border-violet-200 bg-white/80 p-1.5">
          {([
            { id: 'sessions' as const, label: 'Sessions', icon: LayoutList },
            { id: 'stats' as const, label: 'Statistiques', icon: BarChart3 },
            { id: 'calendars' as const, label: 'Calendriers', icon: CalendarDays },
          ]).map(({ id, label, icon: Icon }) => {
            const active = activeTab === id;
            return (
              <button
                key={id}
                type="button"
                onClick={() => setActiveTab(id)}
                className={[
                  'inline-flex flex-1 items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-bold transition-colors sm:flex-none',
                  active
                    ? 'bg-violet-600 text-white shadow-sm shadow-violet-200'
                    : 'text-slate-600 hover:bg-violet-50 hover:text-violet-700',
                ].join(' ')}
              >
                <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
                {label}
              </button>
            );
          })}
        </div>
      ) : null}

      {(activeTab === 'sessions' || !showSuperAdminInsights) ? (
      <div className={`rounded-[2rem] border border-violet-200 bg-gradient-to-br from-violet-50 via-white to-slate-50 p-5 shadow-sm ${showSuperAdminInsights ? 'mt-6' : ''}`}>
        <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="mb-3 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="text-sm font-black uppercase tracking-wider text-slate-500">
              Sessions visibles
              <span className="ml-2 font-semibold normal-case tabular-nums text-slate-400">
                {visibleSessions.length}
              </span>
            </div>
            <div className="flex flex-wrap items-center justify-end gap-2">
              <div className="relative">
                <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  placeholder="Rechercher…"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-44 pl-8 pr-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 sm:w-52"
                />
              </div>
              <label className="flex items-center gap-1.5 text-xs font-medium text-slate-500 whitespace-nowrap">
                <span>Par page</span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => setItemsPerPage(Number(e.target.value) as typeof ITEMS_PER_PAGE_OPTIONS[number])}
                  className="px-2 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  {ITEMS_PER_PAGE_OPTIONS.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </label>
              <ProgramFilterDropdown programs={programs} value={programFilter} onChange={setProgramFilter} />
              <FacilitatorFilterDropdown
                options={facilitatorFilterOptions}
                value={facilitatorFilter}
                onChange={setFacilitatorFilter}
              />
              {hasActiveFilters && (
                <>
                  <button
                    type="button"
                    onClick={() => void copyFilteredViewLink()}
                    className={[
                      'inline-flex items-center gap-1.5 rounded-xl border px-2.5 py-2 text-xs font-semibold transition-colors',
                      copiedFilterLink
                        ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                        : 'border-violet-200 bg-violet-50 text-violet-700 hover:bg-violet-100',
                    ].join(' ')}
                    title="Copier le lien de cette vue filtrée"
                  >
                    <Link2 className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                    {copiedFilterLink ? 'Lien copié' : 'Copier le lien'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setSearchQuery('');
                      setProgramFilter('all');
                      setFacilitatorFilter('all');
                      setPage(1);
                    }}
                    className="px-2 py-2 text-xs font-medium text-slate-500 hover:text-slate-700"
                  >
                    Réinitialiser
                  </button>
                </>
              )}
            </div>
          </div>
          {canBulkArchiveVisible && visibleSessions.length > 0 ? (
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-indigo-200 bg-indigo-50/60 px-4 py-3">
              <label className="inline-flex cursor-pointer items-center gap-2 text-sm font-semibold text-slate-700">
                <input
                  ref={selectAllVisibleRef}
                  type="checkbox"
                  checked={allVisibleSelected}
                  onChange={toggleAllVisibleSelection}
                  className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                />
                Tout sélectionner
              </label>
              {selectedVisibleIds.size > 0 ? (
                <button
                  type="button"
                  onClick={requestArchiveSelectedVisible}
                  className="inline-flex items-center gap-2 rounded-xl bg-amber-600 px-4 py-2 text-sm font-bold text-white transition-colors hover:bg-amber-700"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 7h16m-1 0v10a2 2 0 01-2 2H7a2 2 0 01-2-2V7m3-3h8l1 3H7l1-3z" />
                  </svg>
                  Archiver ({selectedVisibleIds.size})
                </button>
              ) : null}
            </div>
          ) : null}
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs">
                  {canBulkArchiveVisible ? (
                    <th className="w-10 px-4 py-3 align-middle">
                      <span className="sr-only">Sélection</span>
                    </th>
                  ) : null}
                  <SortableTh label="Session" sortKey="name" currentSort={sortKey} sortDir={sortDir} onSort={handleSort} className="px-4 py-3 align-middle min-w-[15rem]" />
                  <SortableTh label="Date" sortKey="anchorDate" currentSort={sortKey} sortDir={sortDir} onSort={handleSort} className="px-4 py-3 align-middle whitespace-nowrap" />
                  <SortableTh label="Statut" sortKey="status" currentSort={sortKey} sortDir={sortDir} onSort={handleSort} className="px-4 py-3 align-middle" />
                  <th
                    className="px-4 py-3 align-middle text-center font-bold text-slate-400 uppercase tracking-wider whitespace-nowrap"
                    title="Mail d'accès envoyé à l'intervenant (auto J-21 ou coche manuelle)"
                  >
                    Mail intervenant
                  </th>
                  <th className="px-4 py-3 align-middle text-right font-bold text-slate-400 uppercase tracking-wider whitespace-nowrap">Actions</th>
                </tr>
              </thead>
              <tbody>
                {visibleSessions.length === 0 ? (
                  <tr>
                    <td colSpan={canBulkArchiveVisible ? 6 : 5} className="px-4 py-10 text-center text-sm text-slate-500">
                      Aucune session visible ne correspond aux filtres courants.
                    </td>
                  </tr>
                ) : (
                  paginatedSessions.map((item) => renderVisibleSessionRow(item))
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 pt-4">
              <div className="text-sm text-slate-500">
                Page {currentPage} sur {totalPages}
                <span className="ml-2">
                  ({(currentPage - 1) * itemsPerPage + 1}–{Math.min(currentPage * itemsPerPage, visibleSessions.length)} sur {visibleSessions.length})
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage <= 1}
                  className="px-3 py-1.5 rounded-lg border border-slate-200 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Précédent
                </button>
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage >= totalPages}
                  className="px-3 py-1.5 rounded-lg border border-slate-200 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Suivant
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="mt-4 overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
          <button
            type="button"
            onClick={() => setShowArchivedAccordion((open) => !open)}
            className="flex w-full items-center justify-between gap-4 bg-white px-5 py-4 text-left"
          >
            <div>
              <div className="text-sm font-black uppercase tracking-wider text-amber-700">Sessions archivées</div>
            </div>
            <div className="flex items-center gap-3">
              <span className="rounded-full bg-white px-3 py-1 text-xs font-bold text-amber-700 ring-1 ring-amber-200">
                {sortedArchivedSessions.length}
              </span>
              <svg
                className={`h-5 w-5 text-amber-700 transition-transform ${showArchivedAccordion ? 'rotate-180' : ''}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </button>

          {showArchivedAccordion && (
            <div className="border-t border-slate-100 px-5 py-4">
              {sortedArchivedSessions.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">
                  Aucune session archivée ne correspond aux filtres courants.
                </div>
              ) : (
                <>
                  {canBulkDeleteArchived ? (
                    <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-amber-200 bg-amber-50/60 px-4 py-3">
                      <label className="inline-flex cursor-pointer items-center gap-2 text-sm font-semibold text-slate-700">
                        <input
                          ref={selectAllArchivedRef}
                          type="checkbox"
                          checked={allArchivedSelected}
                          onChange={toggleAllArchivedSelection}
                          className="h-4 w-4 rounded border-slate-300 text-amber-600 focus:ring-amber-500"
                        />
                        Tout sélectionner
                      </label>
                      {selectedArchivedIds.size > 0 ? (
                        <button
                          type="button"
                          onClick={requestDeleteSelectedArchived}
                          className="inline-flex items-center gap-2 rounded-xl bg-red-600 px-4 py-2 text-sm font-bold text-white transition-colors hover:bg-red-700"
                        >
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                          Supprimer ({selectedArchivedIds.size})
                        </button>
                      ) : null}
                    </div>
                  ) : null}
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="border-b border-slate-100 text-left text-xs">
                          {canBulkDeleteArchived ? (
                            <th className="w-10 px-4 py-3 align-middle">
                              <span className="sr-only">Sélection</span>
                            </th>
                          ) : null}
                          <SortableTh label="Session" sortKey="name" currentSort={sortKey} sortDir={sortDir} onSort={handleSort} className="px-4 py-3 align-middle min-w-[15rem]" />
                          <SortableTh label="Date" sortKey="anchorDate" currentSort={sortKey} sortDir={sortDir} onSort={handleSort} className="px-4 py-3 align-middle whitespace-nowrap" />
                          <SortableTh label="Statut" sortKey="players" currentSort={sortKey} sortDir={sortDir} onSort={handleSort} className="px-4 py-3 align-middle" />
                          <th className="px-4 py-3 align-middle text-right font-bold text-slate-400 uppercase tracking-wider whitespace-nowrap">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedArchivedSessions.map(({ session, anchorDate, players, completed }) => (
                          <tr
                            key={session.id}
                            onClick={() => onEditSession?.(session)}
                            className={[
                              'border-b border-slate-100 text-slate-700 transition-colors',
                              onEditSession ? 'cursor-pointer hover:bg-indigo-50/40' : '',
                            ].join(' ')}
                          >
                            {canBulkDeleteArchived ? (
                              <td className="w-10 px-4 py-2.5 align-middle" onClick={(event) => event.stopPropagation()}>
                                <input
                                  type="checkbox"
                                  checked={selectedArchivedIds.has(session.id)}
                                  onChange={() => toggleArchivedSelection(session.id)}
                                  aria-label={`Sélectionner ${session.name || session.code}`}
                                  className="h-4 w-4 rounded border-slate-300 text-amber-600 focus:ring-amber-500"
                                />
                              </td>
                            ) : null}
                            <SessionMainCell
                              session={session}
                              copiedCode={copiedCode}
                              onCopy={copySessionCode}
                              subtitle={`Archivée${session.archivedReason ? ` (${session.archivedReason})` : ''}`}
                            />
                            <td className="px-4 py-2.5 align-middle whitespace-nowrap text-sm tabular-nums text-slate-600">
                              {formatCompactSchedule(session, anchorDate)}
                            </td>
                            <SessionStatusCell session={session} players={players} completed={completed} />
                            <SessionActionsCell
                              session={session}
                              onEditSession={onEditSession}
                              onUnarchiveSession={onUnarchiveSession}
                              onDeleteSession={onDeleteSession}
                              navigate={navigate}
                            />
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
      ) : null}

      {showSuperAdminInsights && activeTab === 'stats' ? (
      <>
      <div className="mt-6 grid grid-cols-2 gap-4 xl:grid-cols-6">
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Sessions jouées</div>
          <div className="mt-2 text-3xl font-black text-slate-900">{summary.totalSessions}</div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Sessions actives</div>
          <div className="mt-2 text-3xl font-black text-emerald-600">{summary.activeSessions}</div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Archivées</div>
          <div className="mt-2 text-3xl font-black text-amber-600">{summary.archivedSessions}</div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Sessions de test</div>
          <div className="mt-2 text-3xl font-black text-slate-900">{summary.testSessions}</div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Joueurs cumulés</div>
          <div className="mt-2 text-3xl font-black text-slate-900">{summary.totalPlayers}</div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Équipes terminées</div>
          <div className="mt-2 text-3xl font-black text-slate-900">{summary.totalCompleted}</div>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 xl:grid-cols-[1.5fr_1fr]">
        <div className="rounded-3xl border border-slate-200 bg-white p-5">
          <div className="mb-4 flex items-center justify-between gap-4">
            <div>
              <div className="text-sm font-black uppercase tracking-wider text-slate-500">Courbes mensuelles</div>
              <div className="mt-1 text-sm text-slate-500">Sessions jouées, joueurs et équipes terminées sur les 6 derniers mois.</div>
            </div>
            <Link
              to="/admin/sessions/stats"
              className="shrink-0 rounded-xl border border-violet-200 bg-violet-50 px-4 py-2 text-xs font-bold text-violet-700 hover:bg-violet-100"
            >
              Voir plus
            </Link>
          </div>
          {monthlySeries.length > 0 ? (
            <div ref={monthlyChartRef} />
          ) : (
            <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-6 py-10 text-sm text-slate-500">
              Aucune donnée de session à afficher.
            </div>
          )}
        </div>

        <div className="rounded-3xl border border-slate-200 bg-white p-5">
          <div className="flex items-center justify-between gap-4">
            <div className="text-sm font-black uppercase tracking-wider text-slate-500">Stats par mois</div>
            <Link
              to="/admin/sessions/stats"
              className="rounded-xl border border-violet-200 bg-violet-50 px-3 py-1.5 text-xs font-bold text-violet-700 hover:bg-violet-100"
            >
              Voir plus
            </Link>
          </div>
          <div className="mt-4 space-y-3">
            {[...monthlySeries].reverse().map((item) => (
              <div key={item.label} className="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3">
                <div className="flex items-center justify-between gap-4">
                  <div className="font-bold text-slate-900">{item.label}</div>
                  <div className="text-xs font-bold uppercase tracking-wide text-slate-400">{item.sessions} sessions jouées</div>
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-4 text-sm text-slate-600">
                  <span>{item.players} joueurs</span>
                  <span>{item.completed} équipes terminées</span>
                  <span>{item.archived} archivées</span>
                </div>
                {item.sessions > 0 && (
                  <button
                    type="button"
                    onClick={() => handleExportMonth(item.key)}
                    className="mt-3 inline-flex items-center justify-center rounded-xl border border-indigo-200 bg-indigo-50 px-3 py-2 text-xs font-bold text-indigo-700 hover:bg-indigo-100"
                  >
                    Export CSV mensuel
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
      </>
      ) : null}

      {showSuperAdminInsights && activeTab === 'calendars' ? (
      <>
      <div className="mt-6 rounded-3xl border border-slate-200 bg-white p-5">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="text-sm font-black uppercase tracking-wider text-slate-500">Calendriers d’activité</div>
            <div className="mt-1 text-sm text-slate-500">
              Sessions jouées (violet) et séances prévues (vert) — y compris les créneaux programme sans session liée.
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-3">
            <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500">
              <span className="font-bold uppercase tracking-wide text-slate-400">Jouées</span>
              <span className="h-3 w-3 rounded border border-indigo-200 bg-indigo-100" />
              <span className="h-3 w-3 rounded border border-indigo-400 bg-indigo-300" />
              <span className="h-3 w-3 rounded border border-indigo-500 bg-indigo-500" />
              <span className="ml-2 font-bold uppercase tracking-wide text-slate-400">Prévues</span>
              <span className="h-3 w-3 rounded border border-dashed border-emerald-300 bg-emerald-50" />
              <span className="h-3 w-3 rounded border border-dashed border-emerald-500 bg-emerald-200" />
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setCalendarMonthOffset((current) => current + 1)}
                className="inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50"
              >
                ← Mois précédents
              </button>
              <button
                type="button"
                onClick={() => setCalendarMonthOffset(0)}
                disabled={calendarMonthOffset === 0}
                className="inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Aujourd’hui
              </button>
              <button
                type="button"
                onClick={() => setCalendarMonthOffset((current) => current - 1)}
                className="inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50"
              >
                Mois suivants →
              </button>
            </div>
          </div>
        </div>

        <div className="mt-5 grid grid-cols-1 gap-5 xl:grid-cols-3">
          {recentCalendars.map((calendar) => (
            <div key={calendar.key} className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
              <div className="mb-3 text-sm font-bold capitalize text-slate-900">{calendar.title}</div>
              <div className="mb-2 grid grid-cols-7 gap-1 text-center text-[10px] font-bold uppercase tracking-wide text-slate-400">
                {['L', 'M', 'M', 'J', 'V', 'S', 'D'].map((day, index) => (
                  <div key={`${calendar.key}-${day}-${index}`}>{day}</div>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {calendar.cells.map((cell) => {
                  const dayTotal = cell.playedCount + cell.plannedCount;
                  return (
                  <button
                    type="button"
                    key={`${calendar.key}-${cell.isoDate}`}
                    title={`${cell.isoDate} — ${cell.playedCount} jouée(s), ${cell.plannedCount} prévue(s)`}
                    onClick={() => {
                      if (!cell.isCurrentMonth) {
                        return;
                      }

                      setSelectedDayIso(cell.isoDate);
                    }}
                    disabled={!cell.isCurrentMonth}
                    className={[
                      'relative flex aspect-square items-center justify-center rounded-lg border text-[10px] font-bold transition-colors',
                      getCalendarCellClass(cell),
                      cell.isCurrentMonth ? 'cursor-pointer hover:scale-105 hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-400' : 'cursor-default',
                      cell.isCurrentMonth ? 'text-slate-700' : '',
                    ].join(' ')}
                  >
                    {cell.isCurrentMonth ? Number(cell.isoDate.slice(-2)) : ''}
                    {cell.isCurrentMonth && dayTotal > 0 ? (
                      <span className="absolute bottom-0.5 right-0.5 flex gap-px">
                        {cell.playedCount > 0 ? (
                          <span className="h-1.5 w-1.5 rounded-full bg-indigo-600" aria-hidden="true" />
                        ) : null}
                        {cell.plannedCount > 0 ? (
                          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" aria-hidden="true" />
                        ) : null}
                      </span>
                    ) : null}
                  </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {selectedDayIso && (
        <div className="fixed inset-0 z-[120]" role="dialog" aria-modal="true" aria-labelledby="calendar-day-modal-title">
          <div className="fixed inset-0 bg-slate-900/70 backdrop-blur-sm" onClick={() => setSelectedDayIso(null)}></div>
          <div className="fixed inset-0 overflow-y-auto p-4">
            <div className="flex min-h-full items-center justify-center">
              <div className="relative w-full max-w-3xl overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl">
                <button
                  type="button"
                  onClick={() => setSelectedDayIso(null)}
                  className="absolute right-5 top-5 z-10 rounded-xl bg-slate-50 p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
                >
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>

                <div className="border-b border-slate-200 bg-gradient-to-r from-violet-50 to-white px-6 py-6 sm:px-8">
                  <p className="text-xs font-black uppercase tracking-[0.25em] text-violet-600">Détail journée</p>
                  <h3 id="calendar-day-modal-title" className="mt-2 text-2xl font-black capitalize tracking-tight text-slate-900">
                    {formatFullDate(selectedDayIso)}
                  </h3>
                  <p className="mt-2 text-sm text-slate-500">
                    {selectedDayDetails.played.length} jouée{selectedDayDetails.played.length > 1 ? 's' : ''}
                    {selectedDayDetails.plannedSessions.length + selectedDayDetails.plannedSlots.length > 0
                      ? ` · ${selectedDayDetails.plannedSessions.length + selectedDayDetails.plannedSlots.length} prévue${selectedDayDetails.plannedSessions.length + selectedDayDetails.plannedSlots.length > 1 ? 's' : ''}`
                      : ''}
                  </p>
                </div>

                <div className="max-h-[70vh] overflow-y-auto px-6 py-6 sm:px-8">
                  {selectedDayTotal === 0 ? (
                    <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-6 py-10 text-center text-sm text-slate-500">
                      Aucune activité sur cette journée.
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {selectedDayDetails.played.length > 0 ? (
                        <div className="space-y-4">
                          <div className="text-xs font-black uppercase tracking-wider text-indigo-600">Sessions jouées</div>
                          {selectedDayDetails.played.map(({ session, anchorDate, players, completed }) => (
                        <div key={`played-${selectedDayIso}-${session.id}`} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                            <div>
                              <div className="flex items-center gap-3">
                                <span className="text-lg font-black text-slate-900">{session.code}</span>
                                <span className="rounded-full bg-white px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-slate-500">
                                  {session.gameMode}
                                </span>
                                {session.archivedAt && (
                                  <span className="rounded-full bg-amber-100 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-amber-700">
                                    Archivée
                                  </span>
                                )}
                              </div>
                              <div className="mt-1 text-base font-bold text-slate-900">{session.name || 'Sans nom'}</div>
                              <div className="mt-1 text-sm text-slate-500">{formatDateTime(anchorDate)}</div>
                            </div>
                            <div className="grid grid-cols-2 gap-3 sm:min-w-[220px]">
                              <div className="rounded-2xl bg-white px-4 py-3 text-center">
                                <div className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Joueurs</div>
                                <div className="mt-1 text-2xl font-black text-slate-900">{players}</div>
                              </div>
                              <div className="rounded-2xl bg-white px-4 py-3 text-center">
                                <div className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Terminées</div>
                                <div className="mt-1 text-2xl font-black text-slate-900">{completed}</div>
                              </div>
                            </div>
                          </div>

                          <div className="mt-4 flex flex-wrap items-center gap-3 text-sm text-slate-600">
                            {session.programName ? (
                              <span className="rounded-full bg-indigo-50 px-2.5 py-1 text-xs font-bold text-indigo-700 ring-1 ring-indigo-100">
                                {session.programName}
                              </span>
                            ) : null}
                            {session.language ? (
                              <span className="inline-flex items-center gap-1.5">
                                {getLanguageOption(session.language) ? (
                                  <LanguageFlag
                                    countryCode={getLanguageOption(session.language)!.countryCode}
                                    title={getLanguageOption(session.language)!.label}
                                    className="h-3.5 w-5 rounded-sm ring-1 ring-slate-200/80"
                                  />
                                ) : null}
                                {session.language.toUpperCase()}
                              </span>
                            ) : null}
                            {session.timezone ? <span>{timezoneShortLabel(session.timezone)}</span> : null}
                          </div>

                          {session.notes ? (
                            <div className="mt-3 text-sm text-slate-600">
                              <span className="font-bold text-slate-500">Intervenant :</span> {session.notes}
                            </div>
                          ) : null}
                        </div>
                          ))}
                        </div>
                      ) : null}

                      {selectedDayDetails.plannedSessions.length > 0 || selectedDayDetails.plannedSlots.length > 0 ? (
                        <div className="space-y-4">
                          <div className="text-xs font-black uppercase tracking-wider text-emerald-600">Séances prévues</div>
                          {selectedDayDetails.plannedSessions.map(({ session }) => {
                            const scheduleDate = session.startDate ? new Date(session.startDate) : new Date();
                            return (
                              <div key={`planned-session-${session.id}`} className="rounded-2xl border border-dashed border-emerald-200 bg-emerald-50/50 p-4">
                                <div className="flex items-center gap-3">
                                  <span className="text-lg font-black text-slate-900">{session.code}</span>
                                  <span className="rounded-full bg-white px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-emerald-700">
                                    Session planifiée
                                  </span>
                                </div>
                                <div className="mt-1 text-base font-bold text-slate-900">{session.name || 'Sans nom'}</div>
                                <div className="mt-1 text-sm text-slate-500">{formatDateTime(scheduleDate)}</div>
                                {session.programName ? (
                                  <div className="mt-3">
                                    <span className="rounded-full bg-white px-2.5 py-1 text-xs font-bold text-indigo-700 ring-1 ring-indigo-100">
                                      {session.programName}
                                    </span>
                                  </div>
                                ) : null}
                              </div>
                            );
                          })}
                          {selectedDayDetails.plannedSlots.map(({ slot, programName, scheduledAt }) => (
                            <div key={`planned-slot-${slot.id}`} className="rounded-2xl border border-dashed border-emerald-200 bg-emerald-50/50 p-4">
                              <div className="flex items-center gap-3">
                                <span className="rounded-full bg-white px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-emerald-700">
                                  Créneau programme
                                </span>
                                <span className="text-sm font-bold text-indigo-700">{programName}</span>
                              </div>
                              <div className="mt-1 text-base font-bold text-slate-900">
                                {slot.label || slot.location || 'Séance'}
                              </div>
                              <div className="mt-1 text-sm text-slate-500">{formatDateTime(scheduledAt)}</div>
                              {slot.facilitator ? (
                                <div className="mt-3 text-sm text-slate-600">
                                  <span className="font-bold text-slate-500">Intervenant :</span> {slot.facilitator}
                                </div>
                              ) : null}
                            </div>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      </>
      ) : null}

    </section>
  );
};
